import { i18n } from '@lingui/core'
import { normalizeLocalePathname, localeFromPath } from './lib/localePrefix'

export const locales: Record<string, { label: string; flag: string }> = {
  fr: { label: 'Français', flag: '🇫🇷' },
  en: { label: 'English', flag: '🇬🇧' },
  es: { label: 'Español', flag: '🇪🇸' },
  de: { label: 'Deutsch', flag: '🇩🇪' },
  it: { label: 'Italiano', flag: '🇮🇹' },
  nl: { label: 'Nederlands', flag: '🇳🇱' },
  pt: { label: 'Português', flag: '🇵🇹' },
}

export const supportedLocales = Object.keys(locales)
export const defaultLocale = 'fr'
const loadedLocales = new Set<string>()

/** Detecte la locale depuis le pathname (/en/..., /es/...) */
export function getLocaleFromPath(pathname: string): string {
  return localeFromPath(pathname, supportedLocales, defaultLocale)
}

/** Retourne le basename React Router pour la locale */
export function getBasename(locale: string): string {
  return locale === defaultLocale ? '' : `/${locale}`
}

/**
 * Pathname canonique si le préfixe de locale est mal formé, sinon null.
 * Ex: /fr/login → /login ; /en-US/login → /en/login ; /EN/panier → /en/panier.
 * Ces variantes ne matchent pas getLocaleFromPath → basename '' → catch-all 404
 * sans cette correction (cf. lib/localePrefix.ts).
 */
export function normalizeLocalePath(pathname: string): string | null {
  return normalizeLocalePathname(pathname, supportedLocales, defaultLocale)
}

export async function activateLocale(locale: string = defaultLocale) {
  const safeLocale = supportedLocales.includes(locale) ? locale : defaultLocale
  if (!loadedLocales.has(safeLocale)) {
    const { messages } = await import(`./locales/${safeLocale}/messages.mjs`)
    i18n.load(safeLocale, messages)
    loadedLocales.add(safeLocale)
  }
  i18n.activate(safeLocale)
  return safeLocale
}

// Detecter et activer depuis l'URL au chargement
export const initialLocale = typeof window !== 'undefined'
  ? getLocaleFromPath(window.location.pathname)
  : defaultLocale

// Cote client, le bootstrap attend cette promesse avant d'importer App.
// Sinon les constantes traduites au chargement des modules peuvent se figer en FR.
export const localeReady = typeof window !== 'undefined'
  ? activateLocale(initialLocale)
  : Promise.resolve(defaultLocale)

export { i18n }
